import { useState, useCallback, useEffect, useRef } from 'react';

export interface UseKeyboardNavigationOptions {
  /** Total number of items in the list */
  itemCount: number;
  /** Whether keyboard navigation is enabled */
  enabled?: boolean;
  /** Callback when an item is selected (Enter pressed) */
  onSelect?: (index: number) => void;
  /** Whether to wrap around when reaching the end/beginning */
  wrap?: boolean;
}

export interface UseKeyboardNavigationReturn {
  /** Currently focused item index (-1 if none) */
  focusedIndex: number;
  /** Set focused index manually */
  setFocusedIndex: (index: number) => void;
  /** Clear focus (set to -1) */
  clearFocus: () => void;
  /** Handle keyboard event (attach to container) */
  handleKeyDown: (e: React.KeyboardEvent) => void;
  /** Ref to attach to container element for focus management */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Whether container is focused */
  isContainerFocused: boolean;
}

/**
 * Hook for keyboard navigation through a list of items.
 *
 * Supports:
 * - Arrow Up/Down or j/k to navigate
 * - Enter to select
 * - Escape to clear focus
 * - Home/End to jump to first/last item
 *
 * @example
 * const { focusedIndex, handleKeyDown, containerRef } = useKeyboardNavigation({
 *   itemCount: items.length,
 *   onSelect: (index) => handleItemClick(items[index]),
 * });
 *
 * return (
 *   <div ref={containerRef} onKeyDown={handleKeyDown} tabIndex={0}>
 *     {items.map((item, i) => (
 *       <Row key={i} isFocused={i === focusedIndex} />
 *     ))}
 *   </div>
 * );
 */
export function useKeyboardNavigation({
  itemCount,
  enabled = true,
  onSelect,
  wrap = true,
}: UseKeyboardNavigationOptions): UseKeyboardNavigationReturn {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isContainerFocused, setIsContainerFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset focus when item count changes
  useEffect(() => {
    if (focusedIndex >= itemCount) {
      setFocusedIndex(itemCount > 0 ? itemCount - 1 : -1);
    }
  }, [itemCount, focusedIndex]);

  // Track container focus
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocus = () => setIsContainerFocused(true);
    const handleBlur = () => setIsContainerFocused(false);

    container.addEventListener('focus', handleFocus);
    container.addEventListener('blur', handleBlur);

    return () => {
      container.removeEventListener('focus', handleFocus);
      container.removeEventListener('blur', handleBlur);
    };
  }, []);

  const clearFocus = useCallback(() => {
    setFocusedIndex(-1);
  }, []);

  const moveUp = useCallback(() => {
    setFocusedIndex((prev) => {
      if (prev <= 0) {
        return wrap ? itemCount - 1 : 0;
      }
      return prev - 1;
    });
  }, [itemCount, wrap]);

  const moveDown = useCallback(() => {
    setFocusedIndex((prev) => {
      if (prev === -1) {
        return 0; // Start from first item
      }
      if (prev >= itemCount - 1) {
        return wrap ? 0 : itemCount - 1;
      }
      return prev + 1;
    });
  }, [itemCount, wrap]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!enabled || itemCount === 0) return;

      switch (e.key) {
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          moveDown();
          break;

        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          moveUp();
          break;

        case 'Enter':
          if (focusedIndex >= 0 && focusedIndex < itemCount) {
            e.preventDefault();
            onSelect?.(focusedIndex);
          }
          break;

        case 'Escape':
          e.preventDefault();
          clearFocus();
          break;

        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;

        case 'End':
          e.preventDefault();
          setFocusedIndex(itemCount - 1);
          break;

        default:
          break;
      }
    },
    [enabled, itemCount, focusedIndex, moveDown, moveUp, onSelect, clearFocus]
  );

  return {
    focusedIndex,
    setFocusedIndex,
    clearFocus,
    handleKeyDown,
    containerRef,
    isContainerFocused,
  };
}
